/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { AxisLabelsFormatterContextObject } from 'highcharts';
import { ChartProps } from '../../Chart.props';
import { CHART_LEGEND_ALIGNMENT_TYPE_BOTTOM, ChartLegendAlignmentType } from '../../types/ChartLegendAlignment.types';

/**
 * The data object to be used to display a single bar in the chart.
 */
export type BarChartDataItem = {
  id?: string;
  name: string;
  y: number;
  color?: string;
};

/**
 * The props for Bar Chart component.
 */
export interface BarChartProps extends ChartProps {
  /**
   * The label of the X axis.
   */
  axisXLabel?: string;

  /**
   * The minimum value to be set on axis X.
   */
  axisXMinValue?: number;

  /**
   * The maximum value to be set on axis X.
   */
  axisXMaxValue?: number;

  /**
   * The minimum value to be set on axis Y.
   */
  axisYMinValue?: number;

  /**
   * The minimum value to be set on axis Y.
   */
  axisYMaxValue?: number;

  /**
   * The label of the Y axis.
   */
  axisYLabel?: string;

  /**
   * The array of labels to show on the X axis.
   */
  labelsOnXAxis: Array<string>;

  /**
   * A boolean to define if labels on X axis should be shown.
   */
  showLabelsOnXAxis?: boolean;
  /**
   * Additional classes to apply to the chart.
   */
  classes?: string;

  /**
   * Enables you to create custom X axis label templates.
   * https://api.highcharts.com/highcharts/xAxis.labels.formatter
   */
  customXAxisLabelTemplate: (labelObj: AxisLabelsFormatterContextObject) => string;

  /**
   * An array of values to display as a separate bar in each series.
   */
  seriesData: Array<BarChartDataItem>;

  /**
   * The name of series.
   */
  seriesName?: string;

  /**
   * The subtitle of a chart. Can be either a string or a HTML template literal.
   */
  subtitle?: string;
  /**
   * The unit of measurement for the values presented in a bar chart.
   */
  unit?: string;

  /**
   * Determines if a legend should be shown under the chart.
   */
  showLegend?: boolean;

  /**
   * A function which returns a custom tooltip. Return a HTML string template. Available to use 'this' (if not using arrow fn) to refer to a certain point in the chart.
   * https://api.highcharts.com/highcharts/tooltip.formatter
   */
  tooltipContent?: (tooltipObj: Highcharts.TooltipFormatterContextObject) => string;
}

/**
 * Default settings for the Bar Chart.
 */
export const barChartDefaults = {
  alignLegend: CHART_LEGEND_ALIGNMENT_TYPE_BOTTOM as ChartLegendAlignmentType,
  legend: [],
  showLegend: false,
  smooth: true,
  thresholds: [],
  unit: '',
};
